import { useMemo, useCallback } from 'react'
import { ChartOptionElement, ReportElement as GqlReportElement } from 'gqlTypes'
import useReportDetails from 'hooks/useReportDetails'
import useChartOptions from 'hooks/useChartOptions'
import getChartTitle from 'util/getChartTitle'

const useReportElementChart = (element: GqlReportElement, reportId: string) => {
  const { cohort, subgroup, pastResults, config, onChangeConfig } =
    useChartOptions(element.id, ChartOptionElement.Element, undefined, reportId)
  const { parsedData, ...otherReportDetails } = useReportDetails(reportId)
  const parsedContent = useMemo(
    () => JSON.parse(element.content || 'null'),
    [element.content]
  )
  const dataToggles = parsedContent.data_toggles
  // This has caused problems a few times with KM charts. Probably worth spending some time here at some point
  const data =
    parsedData?.elements && parsedContent.data_source_name
      ? parsedData?.elements[parsedContent.data_source_name] || parsedContent
      : parsedContent

  const pastResultNames = (
    data['past-results'] ||
    data['past_results'] ||
    []
  ).map((d: { name: string }) => d.name)
  const staticSegmentationNames = (data['static_segmentations'] || []).map(
    (d: { name: string }) => d.name
  )
  const comparativeNames = (data.comparative || []).map(
    (d: { name: string[] }) => d.name[0]
  )
  let currentNames: string[] = []
  if (['barChart'].includes(data.type)) {
    currentNames = data.current ? [data.current.name] : []
  }
  if (['percentileChart'].includes(data.type)) {
    currentNames = [data.current.name]
  }

  const subgroupHidden = subgroup ? (config?.hidden || {})[subgroup] || [] : []
  const pastResultsHidden = pastResults
    ? (config?.hidden || {})['Past Results'] || []
    : []
  const staticSegmentationHidden =
    (config?.hidden || {})['Static Segmentations'] || []
  const comparativeHidden = (config?.hidden || {})['Comparative'] || []
  const currentHidden = (config?.hidden || {})['Current'] || []
  const hidden = [
    ...subgroupHidden,
    ...pastResultsHidden,
    ...comparativeHidden,
    ...staticSegmentationHidden,
    ...currentHidden,
  ]

  const view = config?.view || 'bubble'

  const handleSetHidden = useCallback(
    (value: string[]) => {
      let newHidden = config && config.hidden ? { ...config.hidden } : {}

      newHidden[subgroup || 'overeall'] = value.filter(
        (v) =>
          !pastResultNames.includes(v) &&
          !staticSegmentationNames.includes(v) &&
          !comparativeNames.includes(v) &&
          !currentNames.includes(v)
      )
      newHidden['Past Results'] = value.filter((v) =>
        pastResultNames.includes(v)
      )
      newHidden['Static Segmentations'] = value.filter((v) =>
        staticSegmentationNames.includes(v)
      )
      newHidden['Comparative'] = value.filter((v) =>
        comparativeNames.includes(v)
      )
      newHidden['Current'] = value.filter((v) => currentNames.includes(v))

      onChangeConfig({ ...config, hidden: newHidden })
    },
    [onChangeConfig]
  )

  const handleSetSort = useCallback(
    (value: null | string | number, direction: 'asc' | 'desc') => {
      if (value === null) {
        let newConfig = { ...config }
        delete newConfig.sort
        onChangeConfig({ ...newConfig })
      } else {
        onChangeConfig({
          ...config,
          sort: { value: value, direction: direction },
        })
      }
    },
    [onChangeConfig]
  )

  const handleSetView = useCallback(
    (value: string) => {
      onChangeConfig({ ...config, view: value })
    },
    [onChangeConfig]
  )

  const elementTitle = getChartTitle(parsedContent, data)

  return {
    cohort,
    subgroup,
    pastResults,
    config,
    onChangeConfig,
    parsedData,
    otherReportDetails,
    parsedContent,
    dataToggles,
    data,
    pastResultNames,
    hidden,
    handleSetHidden,
    view,
    handleSetView,
    handleSetSort,
    elementTitle,
  }
}

export default useReportElementChart
